import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {ComponentDefinition, DocumentInfo, FormDocument, SharedConstants, SystemHeader} from '@formbird/types';
import {BehaviorSubject, forkJoin} from 'rxjs';
import {AbstractComponent, ComponentSetupService } from '@formbird/angular-shared';
import {SclAceEditorService} from '../../../../formbird-sc-shared/src/lib/scl-ace-editor.service';
import {ChangedDocumentService, FormbirdInjectorService} from '@formbird/services';


const logger = console;

// extend the ComponentDefinition type with the additional fields for the component
interface DocumentEditorComponentDefinition extends ComponentDefinition, AfterViewInit {
  aceEditorOptions: any;
  scriptEditMode?: string;
  scriptEditTheme?: string;
  showGutter?: boolean;
  finaliseValue?: any;
  isWrapMode?: any;
  fontSize?: any;
  aceBasePath?: any;
  viMode?: any;
}
class SysHeader extends SystemHeader {
  shouldMakeCurrentVersion?: boolean;
}
interface DocumentEditorFromDocument extends FormDocument {
  systemHeader?: SysHeader;
}

@Component({
  templateUrl: './document-editor.component.html',
  styleUrls: ['./document-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentEditorComponent extends AbstractComponent<DocumentEditorComponentDefinition> implements OnInit, AfterViewInit {

  content: string; // the content for the ace editor. This will be bound to in the editor
  mode: string; // the ace edit mode
  theme: string; // the ace edit theme
  aceBasePath: string; // the ace base path

  editorOptions: any;
  uploadedDocument: any;

  // ace;
  CURRENT_DOCUMENT_ID;
  JSON_INDENT_COUNT = 4;
  MINIMUM_VALID_JSON_LENGTH = 6;
  // aceEditor;
  @ViewChild('editor', {static: false}) aceEditor;

  defaultValue: any;
  msgObj = {};

  isDisabled;
  isShowInfo = false;
  noDirty = undefined;
  isAutoSave: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isUpdatingDocument = false;
  errorMessage = '';
  // tslint:disable-next-line:variable-name
  _session: any;
  tempKeys;
  controlWidth;

  notificationService: any;
  dataService: any;
  modifiedFieldService: any;
  modalService: any;
  mainDocumentListService: any;
  unsavedDocumentService: any;
  currentDocumentService: any;
  offlineUtilService: any;
  documentService: any;
  loggedInUserService: any;
  routerService: any;
  user: any;
  shouldMakeCurrentVersion = false;
  isEnableShouldMakeCurrentVersion = false;
  currentDocumentId;

  textValue: string;

  constructor(
    protected formbirdInjectorService: FormbirdInjectorService,
    private sclAceEditorService: SclAceEditorService,
    protected changedDocumentService: ChangedDocumentService,
    protected componentSetupService: ComponentSetupService,
    protected cdr: ChangeDetectorRef,
    private el: ElementRef
  ) {
    super(formbirdInjectorService, changedDocumentService, componentSetupService, cdr);

    this.notificationService = this.formbirdInjectorService.get(
      'NotificationService'
    );
    this.dataService = this.formbirdInjectorService.get('DataService');
    this.modifiedFieldService = this.formbirdInjectorService.get(
      'ModifiedFieldService'
    );
    this.modalService = this.formbirdInjectorService.get('ModalService');
    this.mainDocumentListService = this.formbirdInjectorService.get(
      'MainDocumentListService'
    );
    this.unsavedDocumentService = this.formbirdInjectorService.get(
      'UnsavedDocumentService'
    );
    this.currentDocumentService = this.formbirdInjectorService.get(
      'CurrentDocumentService'
    );
    this.offlineUtilService = this.formbirdInjectorService.get(
      'OfflineUtilService'
    );
    this.documentService = this.formbirdInjectorService.get('DocumentService');
    this.loggedInUserService = this.formbirdInjectorService.get('LoggedInUserService');
    this.routerService = this.formbirdInjectorService.get('RouterService');
  }

  ngOnInit() {
    this.componentSetupService.setupStandardProperties(this);

    this.user = this.loggedInUserService.user;
    const tplId = this.template.documentId;
    this.defaultValue = {
      documentId: this.offlineUtilService.getNextId(),
      systemHeader: {templateId: tplId, systemType: 'template'}
    };
    this.subs.add(
      this.isAutoSave.subscribe(async value => {
        await this.enableAutoSave(value);
      })
    );

    if (this.document[this.fieldName]) {
      const content = this.document[this.fieldName];
      if (typeof content === 'object') {
        this.content = JSON.stringify(content, undefined, 2);
      } else {
        this.content = content;
      }
    }

    this.mode = this.componentDefinition.scriptEditMode ?? 'json';
    console.log('setting doc editor mode to', this.mode)

    this.theme = this.componentDefinition?.scriptEditTheme ? this.componentDefinition.scriptEditTheme : 'merbivore';

    if (!(typeof this.componentDefinition.aceBasePath === 'undefined' || this.componentDefinition.aceBasePath === null)) {
      this.aceBasePath = this.componentDefinition.aceBasePath;
    }



    if (!this.document.documentId) {
      this.document = this.defaultValue;
    }
    this.CURRENT_DOCUMENT_ID = this.document.documentId;

    const documentUpdatedSub = this.broadcastService
      .on('document:updated')
      .subscribe(data => {
        if (data && data[0].documentId === this.document.documentId) {
          // set isUpdatingDocument to true while the update is happening
          this.isUpdatingDocument = true;

          this.document = data[0];
          this.updateJsonDataToEditor(this.document, true);

          // set isUpdatingDocument back to false after the document has been updated in the editor.
          this.isUpdatingDocument = false;
        }
      });
    this.subs.add(documentUpdatedSub);


    this.renderDocumentDownloaderComponent();

  }

  ngAfterViewInit() {
    this.editorOptions = {
      minLines: 20,
      maxLines: Infinity,
      autoScrollEditorIntoView: true,
      enableSnippets: true,
      wrap: true,
      enableBasicAutocompletion: true,
      enableLiveAutocompletion: true,
      showLineNumbers: true,
      fontSize: this.componentDefinition.fontSize ?? 18,
      ...this.componentDefinition.aceEditorOptions,
    };

    const editor = this.aceEditor.getEditor();

    // Options
    editor.setOptions(this.editorOptions);

    if (this.componentDefinition.viMode || this.user.account.viMode) {
      this.aceEditor.switchToViMode(() => {
        const documentSaveService = this.formbirdInjectorService.get('DocumentSaveService');
        documentSaveService.save(this.formParameters.unsavedDocumentListId);
      })
    }

    editor.commands.addCommand({
      name: 'showOtherCompletions',
      bindKey: 'Ctrl-.',
      exec() {}
    });

    setTimeout(() => {
      this._session = editor.session;

      if (this.componentDefinition.isWrapMode) {
        editor.session.setUseWrapMode(true);
      }

      this.aceEditor.setMode(this.mode);
      this.aceEditor.setTheme(this.theme);
    }, 100);

    // Add Fullscreen Btn in the ACE Editor
    // fullscreen_editor(aceEditor);
    this.sclAceEditorService.enableFullscreen(editor, this.fieldName);

    let text: string;
    if (this.uploadedDocument) {
      text = JSON.stringify(
        this.uploadedDocument,
        null,
        this.JSON_INDENT_COUNT
      );
      this.uploadedDocument = null;
    } else {
      text = JSON.stringify(this.document, null, this.JSON_INDENT_COUNT);
    }

    this.aceEditor.value = text; // this.aceEditor.setValue(text, 1);
    this.tempKeys = [];
    this.controlWidth = this.getControlWidth(this.fullWidth, null);

    let debounce;

    const handleEditorChange = async () => {
      if (this.isUpdatingDocument) {
        this.noDirty = false;
        return;
      }

      this.textValue = this._session.getValue();
      try {
        const jsonData = JSON.parse(this.textValue);

        if (jsonData.documentId && this.currentDocumentId !== jsonData.documentId) {
          this.currentDocumentId = jsonData.documentId;

          try {
            this.aceEditor.setReadOnly(true);
            const data = await this.dataService.getDocument(this.currentDocumentId);
            if (data) {
              // keep createdDate to avoid wrong saving mode from update to insert
              jsonData.systemHeader.createdDate = data.systemHeader.createdDate ? data.systemHeader.createdDate : new Date();
            }
          } catch (err) {
            console.log('Document not found. Insert it: ', this.currentDocumentId);
          } finally {
            this.aceEditor.setReadOnly(false);
          }
        }

        if (!jsonData.documentId) {
          if (this.isShowInfo){
            this.updateJsonDataToEditor(jsonData, null);
          } else {
            jsonData.documentId = this.offlineUtilService.getNextId();
            this.addDocumentsToList(jsonData);
          }

        } else {

          this.resetDoc(jsonData);
        }

        if (this.noDirty === true) {
          this.modifiedFieldService.resetFormDirty();
          this.noDirty = undefined;

        } else {

          setTimeout(() => {
            const fieldName = this.componentDefinition.name;
            this.modifiedFieldService.addModifiedField(
              this.document.documentId,
              fieldName,
              this.formParameters.unsavedDocumentListId,
              false
            );
          }, 500);

        }

        this.errorMessage = null;

        this.document = jsonData;

        const docInfo: DocumentInfo = {
          documentId: this.document.documentId,
          overrideTemplateId: this.template.documentId
        };
        this.changedDocumentService.setDocumentInfo(this.formParameters.unsavedDocumentListId, docInfo);

      } catch (ex) {
        this.errorMessage = ex;
        this.modifiedFieldService.removeModifiedField(
          this.document.documentId,
          this.fieldName,
          this.formParameters.unsavedDocumentListId,
          false
        );
      }
      this.detectChanges();
    };

    editor.on('change', () => {
      if (debounce) {
        clearTimeout(debounce);
      }

      debounce = setTimeout(() => {
        handleEditorChange().then(() => {});
      }, 500);
    });

    // Process editor contents to document on saving
    this.componentDefinition.finaliseValue = () => {
      this.prepareForSave();
    };

  }

  getValue() {
    console.log(this.aceEditor?.value);
  }

  getControlWidth(fullWidth, type) {
    if (typeof type === 'undefined') {
      if (fullWidth && fullWidth === true) {
        return 'col-md-11';
      } else {
        return 'col-md-9 col-lg-8';
      }
    } else if (type === 'Date') {
      // Special case with single directive date because there is an element that occupies
      // an extra space on the column
      if (fullWidth && fullWidth === true) {
        return 'col-md-10';
      } else {
        return 'col-md-8 col-lg-7';
      }
    }
  }

  preview() {
    if (this.disabled) {
      return;
    }
    if (!this.isDisabled) {
      window.open('form/' + this.document.documentId, '_blank');
    }
  }

  private renderDocumentDownloaderComponent() {
    const customElement = this.el.nativeElement.querySelector('sc-document-downloader');

    // pass the parameters of the component to the custom element
    customElement.componentDefinition = this.componentDefinition;
    customElement.document = this.document;
    customElement.template = this.template;
    customElement.isOpenInDocumentEditor = true;
  }

  async verifyJsonData(jsonData): Promise<{op: string, data: any}> {
    const includeDeleted = true;
    try {
      if (!jsonData.documentId) {
        return {
          op: 'Inserted',
          data: await this.dataService.insert(jsonData)
        }
      }
      const found = await this.dataService.documentExists(jsonData.documentId, includeDeleted);
      if (found) {

        let data;
        try {
          data = await this.dataService.getDocument(jsonData.documentId);
        } catch (e) {
          if (e.name === 'DeletedError') {
            throw e;
          }
        }

        try {
          if (data) {
            console.log('data = ', data)
            // NotificationService.warning("The uploaded document contains an existing documentId. The document " +
            //     jsonData.documentId + " will be updated.");
            this.msgObj[data.documentId] = 'Updated';
            return {
              op: 'Updated',
              data: await this.dataService.deepDiffUpdate(data, jsonData)
            }
          }
        } catch (e) {
        }
      }

      this.msgObj[jsonData.documentId] = 'Inserted';
      return {
        op: 'Inserted',
        data: await this.dataService.insert(jsonData)
      }
    } catch (e) {
      this.notificationService.error(`Error in document-editor.component: ${e.error ? e.error: e.message ? e.message: 'at verifyJsonData()'}`);
      throw e
    }
  }

  clearFormDirty() {
    setTimeout(() => {
      this.modifiedFieldService.resetFormDirty();
    }, 3000);
  }

  async insertJsonArray(jsonData) {
    const promises = [];
    let infoFlag = false;

    try {
      if (typeof jsonData === 'string') {
        jsonData = JSON.parse(jsonData);
      }
      // saving a single doc
      if (!Array.isArray(jsonData)) {
        const {op, data} = await this.verifyJsonData(jsonData);
        await this.updateScript(data);
        this.clearFormDirty();
        this.notificationService.success(`${op} document with id \n ${data.documentId}`);
        return;
      }
      // saving multiple documents
      infoFlag = true;
      await (async () => {
        for await (const jsonDataVal of jsonData) {
          console.log('for loop await',jsonDataVal);
          promises.push(await this.verifyJsonData(jsonDataVal));
        }
        const result:any = promises;
        let incRes= 0;
        const savedInfo = {'Multiple Documents': 'Loaded'};
        for await (const promiseRetVal of result) {
          if(promiseRetVal){
            const {op, data} = promiseRetVal;
            const msg = op + ' document ' + data.documentId + ' success';
            this.notificationService.success(msg);
            console.log('success, this.msg = ' + this.msgObj[data.documentId]);
            if (infoFlag) {
              savedInfo[data.documentId] = op;
            }
          }else{
            console.log('jsonData['+incRes+'] :',jsonData[incRes], ' - Returns undefined result when saving/updating documents');
            savedInfo['jsonData'+incRes+''] = 'jsonData['+incRes+']:'+ ' - Returns undefined result when saving/updating documents';
          }
          incRes++;
        }
        if (!infoFlag) {
          this.aceEditor.setReadOnly(false);
        } else {
          this.isShowInfo = true;
          this.updateJsonDataToEditor(savedInfo, null);
          this.aceEditor.setReadOnly(true);
        }
        this.clearFormDirty();
      })();

    } catch (err) {
      this.notificationService.error(err);
      throw err
    }
  }

  onChangeAutoSave() {
    const currentValue = this.isAutoSave.value;
    if (currentValue) {
      this.isAutoSave.next(false);
    } else {
      this.isAutoSave.next(true);
    }
    console.log('onChangeAutoSave() value = ' + this.isAutoSave.value);
  }

  async enableAutoSave(newVal): Promise<boolean> {
    const result = true;
    console.log('watchIsAutoSave, newVal = ' + newVal);
    if (newVal === false && this.isShowInfo === true) {
      this.isShowInfo = false;
      this.aceEditor.setReadOnly(newVal);
      if (this.document) {
        if (this.document.documentId) {
          this.dataService.getDocument(this.document.documentId).then(
            (data) => {
              if (data) {
                this.updateJsonDataToEditor(data);
                this.clearFormDirty();
              }
            },
            () => {
              this.updateJsonDataToEditor(this.defaultValue);
              this.clearFormDirty();
            }
          );
        } else if (this.document['Multiple Documents']) {
          this.updateJsonDataToEditor(this.defaultValue, false);
          this.clearFormDirty();
        }
      }
    }
    return result;
  }

  addDocumentsToList(jsonData, updateHeader?) {

    if (updateHeader !== false) {
      this.initHeader(jsonData);
    }

    this.resetDoc(jsonData);

    this.updateJsonDataToEditor(jsonData, this.isAutoSave.value);
  }

  resetDoc(jsonData) {
    const documentListId = this.formParameters.unsavedDocumentListId;

    this.unsavedDocumentService.addDocumentToUnsavedList(jsonData, documentListId);
    this.unsavedDocumentService.addTemplateToUnsavedList(jsonData, this.template, documentListId);
  }

  initHeader(jsonData) {
    // leave only the systemType, templateId, summaryName, and summaryDescription under the systemHeader.
    // Other fields in the systemHeader could cause problems when the template is saved
    const newSystemHeader = {
      systemType: '',
      templateId: '',
      summaryName: '',
      summaryDescription: '',
      currentVersion: undefined
    };

    if (jsonData.systemHeader) {
      if (jsonData.systemHeader.systemType) {
        newSystemHeader.systemType = jsonData.systemHeader.systemType;
      }

      if (jsonData.systemHeader.templateId) {
        newSystemHeader.templateId = jsonData.systemHeader.templateId;
      }

      if (jsonData.systemHeader.summaryName) {
        newSystemHeader.summaryName = jsonData.systemHeader.summaryName;
      }

      if (jsonData.systemHeader.summaryDescription) {
        newSystemHeader.summaryDescription =
          jsonData.systemHeader.summaryDescription;
      }

      if (jsonData.systemHeader.currentVersion === undefined) {
        newSystemHeader.currentVersion = true;
      }
    }

    jsonData.systemHeader = newSystemHeader;
  }

  updateJsonDataToEditor(jsonData, isDirty = false) {
    console.log('Update input template into template editor.');

    const stringData = JSON.stringify(jsonData, null, this.JSON_INDENT_COUNT);

    if (this._session) {
      const textValue = this._session.getValue();
      if (textValue !== stringData) {
        this.noDirty = isDirty;
        // let cursorPos = this.aceEditor.getCursorPosition();
        this.aceEditor.value = stringData; // this.aceEditor.setValue(stringData, -1);
        // this.aceEditor.moveCursorTo(cursorPos.row, cursorPos.column);
      }
    }

  }

  async updateScript(script) {

    this.shouldMakeCurrentVersion = false;
    try {
      let jsonData;
      if (typeof script === 'string') {
        jsonData = JSON.parse(script);
      } else {
        jsonData = script;
      }

      if (Array.isArray(jsonData)) {
        // this.isAutoSave.next(true);
        await this.enableAutoSave(true);
        this.insertJsonArray(jsonData);
        return;
      }

      this.aceEditor.setReadOnly(false);

      const docId = jsonData.documentId;
      if (docId) {
        this.dataService.getDocument(docId).then(
          data => {
            if (data) {
              this.changedDocumentService.setOrgDocument(data);

              this.uploadedDocument = jsonData;
              this.modifiedFieldService.resetFormDirty();
              this.routerService.rewriteUrl( '/form/' + docId + '/' + this.template.documentId, data.systemHeader.summaryName);
              this.addDocumentsToList(jsonData, false);
              return;
            }

            this.addDocumentsToList(jsonData);

          },
          err => {

            if (err.name === 'ForbiddenError') {

              this.modalService.showDialog('Access Restricted', 'ERROR');

            } else if (err.name === 'DeletedError') {

              this.isEnableShouldMakeCurrentVersion = true;
              const msg =  'The uploaded document has been deleted. The document can be set to current version and' +
                ' will be inserted with this id: ' + docId;
              logger.log(msg);
              this.notificationService.warning(msg);
              this.addDocumentsToList(jsonData);

            } else {

              const msg = 'The uploaded document contains a documentId. The document ' +
                ' will be inserted with this id: ' + docId;
              logger.log(msg);
              this.notificationService.warning(msg);

              this.addDocumentsToList(jsonData, false);
            }
          }
        );
      } else {
        jsonData.documentId = this.offlineUtilService.getNextId();
        this.addDocumentsToList(jsonData, false);
        this.notificationService.warning(`The document will be inserted with id: ${jsonData.documentId}`);
      }

    } catch (err) {

      this.notificationService.error('Update script error: ' + err);

    }
  }

  isJsonExtension(file, extensions) {
    const jsonFile =  file as HTMLInputElement;
    const fileName = jsonFile?.value;
    return (new RegExp('(' + extensions.join('|').replace(/\./g, '\\.') + ')$')).test(fileName);
  }

  onFileSelect(file,files) {
    if (!this.isJsonExtension(file, ['.json'])) {
      const errorMsgJsonExtensionOnly = 'Upload file must be json and have a json extension';
      logger.error(errorMsgJsonExtensionOnly);
      this.notificationService.error(errorMsgJsonExtensionOnly);
      return;
    }
    console.log(files);

    const reader = new FileReader();
    reader.onload = (e) => {

      try {
        if (this.modifiedFieldService.isDirty()) {

          this.modalService.showDialog(
            'There are unsaved changes in the template editor. ' +
            'Do you wish to discard these?',
            'WARNING',
            () => {
              this.updateScript(e.target.result).then(() => {});
            },
            () => {
              console.log('Dismiss the confirm dialog');
            }
          );

        } else {

          if (this.isAutoSave.value === true) {
            this.insertJsonArray(e.target.result);
          } else {
            this.updateScript(e.target.result).then(() => {});
          }
        }

      } catch (err) {

        this.errorMessage = 'Template is not valid JSON';

        this.modifiedFieldService.removeModifiedField(
          this.document.documentId,
          this.fieldName,
          this.formParameters.unsavedDocumentListId,
          false
        );
      }
    };

    if (files && files.length > 0) {
      reader.readAsText(files[0]);
    }
  }

  watchChangeFontSize(fontSize) {
    this.editorOptions.fontSize = fontSize ? fontSize : 18;

    const editor = this.aceEditor.getEditor();
    editor.setOptions(this.editorOptions);
  }

  prepareForSave() {

    const unsavedDocListId = this.formParameters.unsavedDocumentListId;

    const textValue = this.aceEditor.value;
    if (textValue && textValue.length >= this.MINIMUM_VALID_JSON_LENGTH) {

      try {
        this.document = JSON.parse(textValue);
        if (!this.document.documentId) {
          this.document.documentId = this.unsavedDocumentService.getUnsavedDocumentId(unsavedDocListId);
        }

        if (!this.formParameters.overrideTemplateId) {
          if (!this.document.systemHeader) {
            this.document.systemHeader = {};
          }
          if (!this.document.systemHeader.templateId) {
            this.document.systemHeader.templateId = this.unsavedDocumentService.getUnsavedTemplateId(unsavedDocListId);
          }
          if (!this.document.systemHeader.systemType) {
            this.document.systemHeader.systemType = SharedConstants.SYSTEM_TYPE_TEMPLATE;
          }
        }

        this.updateDocumentToStore(this.document);

        const overrideTemplateId = this.formParameters.overrideTemplateId;
        if (!overrideTemplateId) {
          const docInfo: DocumentInfo = {
            overrideTemplateId: this.template.documentId,
            showFormAfterCreated: true
          };
          this.changedDocumentService.setDocumentInfo(unsavedDocListId, docInfo);
        }

        this.errorMessage = null;

      } catch (err) {

        this.errorMessage = 'Template is not valid JSON';

        this.modifiedFieldService.removeModifiedField(
          this.document.documentId,
          this.fieldName,
          unsavedDocListId,
          false);

      }
    }
  }

  private updateDocumentToStore(document) {
    this.changedDocumentService.setDocument(document);
  }

  onChangeMakeCurrentVersion(event) {

    if (event && event.checked && this.document) {
      if (!this.document.systemHeader) {
        this.document.systemHeader = {};
      }

      // systemHeader.shouldMakeCurrentVersion property is used to the server side to set currentVersion false for
      // deleted record and inserting the uploaded document with the new versionId after removing systemHeader.shouldMakeCurrentVersion,

      (this.document as DocumentEditorFromDocument).systemHeader.shouldMakeCurrentVersion = true;
    } else if (event && !event.checked && this.document && this.document.systemHeader) {

      delete (this.document as DocumentEditorFromDocument).systemHeader.shouldMakeCurrentVersion;
    }
  }

}
